// Run-state presentational components ("Read the messages", step 4).
//
// Pure + props-driven on purpose: the wiring layer (App.tsx step-3 pane) computes
// `phaseIndex` and the milestone counts from `phase.kind` + SSE events and passes
// them in. Nothing here binds to data directly, so it's unit-testable in isolation.
//
// Honesty contract (see docs/topics/run-state-redesign/component-motion-spec.md):
//   - continuous motion is INDETERMINATE ("a stage is running"), never a % or rate
//   - real progress = discrete milestone REVEALS at true stage boundaries
//   - the run screen stays GENERAL — counts only, no pattern names/quotes
//   - education content is clearly separate from findings, non-diagnostic
import {
  useEffect,
  useRef,
  useState,
  type TouchEvent as ReactTouchEvent,
  type TransitionEvent as ReactTransitionEvent,
} from "react";
import type { SseEvent } from "../lib/api";
import { deriveMilestones } from "../lib/run-milestones";
import type { HowHeardSource } from "../lib/wizard-events";

// 0 Reading · 1 Grounding · 2 Final review. -1 = not started (setup).
export type RunPhase = -1 | 0 | 1 | 2;
export const RUN_PHASES = ["Reading the record", "Grounding in quotes", "Final review"] as const;
const RAIL_LABELS = ["Reading", "Grounding", "Review"] as const;

// ---- indeterminate "alive" pulse (binds to nothing) ----
export function Pulse() {
  return (
    <span className="rs-orb" aria-hidden="true">
      <span className="rs-orb-halo" />
      <span className="rs-orb-core" />
    </span>
  );
}

// ---- discrete phase rail: lights segments at real boundaries, never a fake fill ----
// The "alive" pulse sits in the SAME row as the bars (vertically centred on them);
// the labels sit below, left-inset so they line up under the bars.
export function PhaseRail({ phase }: { phase: RunPhase }) {
  return (
    <div className="rs-rail">
      <div className="rs-segs-row">
        <Pulse />
        <div className="rs-segs">
          {RAIL_LABELS.map((_, i) => {
            const state = i < phase ? "done" : i === phase ? "active" : "future";
            return <div key={i} className={`rs-seg rs-seg-${state}`} />;
          })}
        </div>
      </div>
      <div className="rs-lbls">
        {RAIL_LABELS.map((lbl, i) => {
          const cls = i < phase ? "was" : i === phase ? "on" : "";
          return (
            <span key={lbl} className={cls}>
              {lbl}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ---- milestone reveals (not counters) ----
// patternCount: shown once Discovery completes. grounded/groundedTotal: once fan-out
// completes. Pre-discovery, a muted placeholder. aria-live so SR announces reveals.
export function MilestoneBadges({
  surfaced,
  patternCount,
  grounded,
  groundedTotal,
}: {
  surfaced: boolean;
  patternCount: number | null;
  grounded: number | null;
  groundedTotal: number | null;
}) {
  return (
    <div className="rs-reveals" aria-live="polite">
      {!surfaced && (
        <span className="rs-badge rs-badge-muted">looking across every message…</span>
      )}
      {surfaced && patternCount !== null && (
        <span className="rs-badge">
          <span className="rs-n">{patternCount.toLocaleString()}</span>&nbsp;patterns surfaced
        </span>
      )}
      {grounded !== null && groundedTotal !== null && (
        <span className="rs-badge">
          grounded <span className="rs-n">{grounded.toLocaleString()}</span> of{" "}
          {groundedTotal.toLocaleString()}
        </span>
      )}
    </div>
  );
}

// ---- education rotator (RUN-ONLY) ----
export type EduCard = {
  tag: string;
  title: string;
  text: string;
  // optional new-tab "Learn more" — none wired in v1 (text-first).
  link?: string;
};

// Plain-language, ~2-sentence cards drawn from the real pattern catalog
// (prompts/cc-analysis/patterns/*.yaml) + frameworks. Non-diagnostic.
export const EDU_CARDS: EduCard[] = [
  {
    tag: "Tactic Aurora looks for",
    title: "Scheduling as time leverage",
    text: "Booking a child’s appointments, therapies, or activities during the other parent’s time erodes that time without negotiation or consent. On its own each instance looks reasonable — the pattern across the record is the point.",
  },
  {
    tag: "Tactic Aurora looks for",
    title: "Economic leverage",
    text: "Using the cost of a child’s care — medical bills, insurance, therapy — as a lever to extract compliance. Withheld payments and refused coordination quietly do the work of control.",
  },
  {
    tag: "How Aurora reads",
    title: "A pattern, not an incident",
    text: "What makes a record one of coercive control rather than ordinary conflict is the constellation of behaviors over time. That’s hard to see message by message — which is exactly what Aurora reads the whole record for.",
  },
  {
    tag: "Grounded in research",
    title: "Stark · Fontes · Katz",
    text: "Aurora’s catalog comes from the established literature on coercive control — Evan Stark, Lisa Aronson Fontes, and Emma Katz. It isn’t invented for the product, and every brief cites the sources it draws on.",
  },
  {
    tag: "Your packet",
    title: "Every claim, footnoted",
    text: "Your brief names each pattern Aurora finds and links it to the exact messages behind it. A reader — your attorney, an advocate, the court — can check every claim against its source.",
  },
  {
    tag: "Tactic Aurora looks for",
    title: "Isolation",
    text: "Cutting a person off from support — friends, family, money, their own information — until the relationship becomes their only reference point. In a co-parenting record it can look like controlling who the children may mention, or forcing every exchange through one tightly policed channel.",
  },
  {
    tag: "Tactic Aurora looks for",
    title: "Monitoring and surveillance",
    text: "Demands to share location, passwords, or constant check-ins — framed as concern or co-parenting diligence. The effect is steady monitoring and reduced autonomy, traced across many small requests rather than any single one.",
  },
  {
    tag: "How Aurora reads",
    title: "Reasonable on its face",
    text: "Controlling messages are often written to look reasonable to an outside reader — polite, concerned, on the record. Aurora weighs what the pattern does across the whole record, not how any single message sounds in isolation.",
  },
  {
    tag: "How Aurora reads",
    title: "What Aurora won’t do",
    text: "Aurora won’t invent patterns that aren’t in your messages, and a pattern it doesn’t surface isn’t proof none exists. It’s a reading aid for a human decision-maker — you, your attorney, the court — never a diagnosis or a verdict.",
  },
];

const CARD_INTERVAL_MS = 16000; // calm cadence (was 13s, +3s); paused on hover/focus/touch + reduced-motion

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export function EducationRotator({
  cards = EDU_CARDS,
  compact = false,
}: {
  cards?: EduCard[];
  /** Batched hand-off variant (Rich, wait-screen mock 2026-06-11): no head
   *  row ("While Aurora scans" kicker gone — vertical room), the tag chip
   *  moves inside each slide, paddings tightened via .rs-edu--compact. */
  compact?: boolean;
}) {
  const n = cards.length;
  const reduce = prefersReducedMotion();
  // Infinite carousel: the track carries [lastClone, ...cards, firstClone], so
  // real card i lives at slide position i+1. A wrap (last→first / first→last)
  // animates ONE card-width onto the adjacent clone, then we snap — with the
  // transition off — to the real card it duplicates. That single step replaces
  // the old translateX(-idx*100%) sweep that flashed back through every card.
  const [pos, setPos] = useState(1);
  const [animate, setAnimate] = useState(true);
  const pausedRef = useRef(false);
  const touchStartXRef = useRef<number | null>(null);

  const boundedPos = Math.min(Math.max(pos, 0), n + 1);
  const realIdx = (((boundedPos - 1) % n) + n) % n;

  // In reduced-motion there's no transition (so no transitionend to snap on);
  // navigate by plain modulo and never traverse a clone. This DEPENDS on the CSS
  // `@media (prefers-reduced-motion: reduce) { .rs-edu-track { transition: none } }`
  // in styles.css matching this same query — keep the two in sync, or a reduced-
  // motion user gets the full translateX sweep back.
  // Steps CLAMP at the clone boundaries (0 and n+1): the wrap-snap only
  // fires when the transition ends exactly on a clone, so an unbounded
  // increment (fast arrow clicks, or auto-advance while the tab is hidden
  // and transitions don't run) walked the track past the last slide into
  // empty space — a permanently blank card. At the boundary, extra clicks
  // wait out the snap (a beat of damping at the wrap, never a blank).
  const next = () =>
    reduce ? setPos(((realIdx + 1) % n) + 1) : setPos((p) => Math.min(p + 1, n + 1));
  const prev = () =>
    reduce ? setPos(((realIdx - 1 + n) % n) + 1) : setPos((p) => Math.max(p - 1, 0));
  const go = (i: number) => setPos(i + 1);

  // Auto-advance, unless reduced-motion or paused (hover/focus/touch).
  useEffect(() => {
    if (reduce) return;
    const t = window.setInterval(() => {
      // document.hidden: transitions don't run in background tabs, so the
      // snap never fires — advancing would overrun the clone (see next()).
      if (!pausedRef.current && !document.hidden) {
        setPos((p) => Math.min(p + 1, n + 1));
      }
    }, CARD_INTERVAL_MS);
    return () => window.clearInterval(t);
  }, [reduce]);

  // When a wrap step lands on a clone, jump (transition off) to the real card
  // it duplicates. Guard to the track's own transform end — not bubbled events.
  const onTransitionEnd = (e: ReactTransitionEvent) => {
    if (e.target !== e.currentTarget || e.propertyName !== "transform") return;
    if (pos === n + 1) {
      setAnimate(false);
      setPos(1);
    } else if (pos === 0) {
      setAnimate(false);
      setPos(n);
    }
  };
  // Re-arm the transition one frame after a no-animation snap commits.
  useEffect(() => {
    if (animate) return;
    const r = window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => setAnimate(true)),
    );
    return () => window.cancelAnimationFrame(r);
  }, [animate]);

  // iPad-style swipe: pause auto-advance while a finger is down; advance or
  // retreat on a horizontal flick past a small threshold.
  const onTouchStart = (e: ReactTouchEvent) => {
    pausedRef.current = true;
    touchStartXRef.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: ReactTouchEvent) => {
    const start = touchStartXRef.current;
    touchStartXRef.current = null;
    pausedRef.current = false;
    if (start == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? start) - start;
    if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
  };

  const slides = [cards[n - 1], ...cards, cards[0]];

  return (
    <div
      className={`rs-edu${compact ? " rs-edu--compact" : ""}`}
      // Not an aria-live region: rotations must NOT be announced.
      aria-roledescription="carousel"
      aria-label="About coercive control"
      onMouseEnter={() => (pausedRef.current = true)}
      onMouseLeave={() => (pausedRef.current = false)}
      onFocusCapture={() => (pausedRef.current = true)}
      onBlurCapture={() => (pausedRef.current = false)}
    >
      {!compact && (
        <div className="rs-edu-head">
          <span className="rs-edu-tag">{cards[realIdx].tag}</span>
          <span className="rs-edu-kicker">While Aurora scans · about coercive control</span>
        </div>
      )}
      {/* iPad-style paged slider: every card lives in a flex track that
          translates horizontally. Swipeable on touch, arrow/dot driven on
          desktop. The viewport's min-height keeps the controls from jumping as
          cards flip. */}
      <div
        className="rs-edu-viewport"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="rs-edu-track"
          style={{
            transform: `translateX(-${boundedPos * 100}%)`,
            ...(animate ? null : { transition: "none" }),
          }}
          onTransitionEnd={onTransitionEnd}
        >
          {slides.map((c, i) => (
            <div className="rs-edu-slide" key={i} aria-hidden={i !== pos}>
              {compact && <span className="rs-edu-tag">{c.tag}</span>}
              <div className="rs-edu-title">{c.title}</div>
              <p className="rs-edu-text">{c.text}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="rs-edu-ctrls">
        <button type="button" aria-label="Previous" onClick={prev}>
          ‹
        </button>
        <div className="rs-dots">
          {cards.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`rs-dot${i === realIdx ? " on" : ""}`}
              aria-label={`Card ${i + 1} of ${n}`}
              aria-current={i === realIdx}
              onClick={() => go(i)}
            />
          ))}
        </div>
        <button type="button" aria-label="Next" onClick={next}>
          ›
        </button>
      </div>
      {reduce && <div className="rs-edu-rm">Auto-advance off — use ‹ ›</div>}
    </div>
  );
}

// ---- "where did you hear about Aurora?" (one optional question) ----
// Wears the reading carousel's dress ON PURPOSE (Rich, how-heard mocks
// 2026-08-06): the same .rs-edu--compact shell and the same vertical anatomy
// — content inside a 112px viewport-equivalent, controls in the foot row —
// so it OCCUPIES the carousel's slot at the carousel's exact size and swaps
// out for it once answered or skipped. Six sources answer in one tap;
// "Someone referred me" and "Other" flip the card in place to a fill-in.
// The card never blocks anything and never comes back once resolved (App
// latches the answer in localStorage).

const HOW_HEARD_CHIPS: { source: HowHeardSource; label: string }[] = [
  { source: "facebook", label: "Facebook" },
  { source: "private-group", label: "A private group" },
  { source: "linkedin", label: "LinkedIn" },
  { source: "web-search", label: "Web search" },
  { source: "ai-chat", label: "Asked an AI" },
  { source: "talk-podcast", label: "A talk or podcast" },
  { source: "referral", label: "Someone referred me" },
  { source: "other", label: "Other" },
];

/** What RunHandoff needs from App to run the question: where it stands and
 *  what to do with the outcome. "thanked" is answered-this-session (shows
 *  the thanks line); "done" is skipped, or resolved in some earlier session
 *  (shows nothing). */
export type HowHeardProps = {
  state: "open" | "thanked" | "done";
  onAnswer: (source: HowHeardSource, detail: string | null) => void;
  onSkip: () => void;
};

export function HowHeardCard({
  onAnswer,
  onSkip,
}: {
  onAnswer: (source: HowHeardSource, detail: string | null) => void;
  onSkip: () => void;
}) {
  // "choices" is the chip face; "referral" / "other" are the fill-in faces.
  const [face, setFace] = useState<"choices" | "referral" | "other">("choices");
  const [detail, setDetail] = useState("");
  const send = () => {
    if (face === "choices") return;
    const trimmed = detail.trim();
    onAnswer(face, trimmed ? trimmed : null);
  };

  if (face !== "choices") {
    return (
      <div className="rs-edu rs-edu--compact hh-slot" aria-label="One optional question">
        <div className="hh-body">
          <p className="rs-edu-title">
            {face === "referral" ? "Someone referred me —" : "Other —"}
          </p>
          <div className="hh-follow">
            <input
              type="text"
              autoFocus
              maxLength={200}
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") send();
              }}
              placeholder={
                face === "referral"
                  ? "Who referred you? A first name is plenty."
                  : "Where did you come across Aurora?"
              }
            />
            <button type="button" className="hh-send" onClick={send}>
              Send
            </button>
          </div>
          <p className="hh-followsub">Rather not say? Just hit Send.</p>
        </div>
        <div className="hh-foot">
          <p className="rs-edu-tag">One optional question</p>
          <button
            type="button"
            className="hh-back"
            onClick={() => {
              setDetail("");
              setFace("choices");
            }}
          >
            ‹ back to the choices
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rs-edu rs-edu--compact hh-slot" aria-label="One optional question">
      <div className="hh-body">
        <p className="rs-edu-title">Where did you hear about Aurora?</p>
        <div className="hh-chips">
          {HOW_HEARD_CHIPS.map((c) => (
            <button
              key={c.source}
              type="button"
              className="hh-chip"
              onClick={() =>
                c.source === "referral" || c.source === "other"
                  ? setFace(c.source)
                  : onAnswer(c.source, null)
              }
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div className="hh-foot">
        <p className="rs-edu-tag">One optional question</p>
        <span className="hh-foot-hint">One tap — it&rsquo;s the only thing we&nbsp;ask.</span>
        <button type="button" className="hh-dismiss" onClick={onSkip}>
          No thanks
        </button>
      </div>
    </div>
  );
}

/** The quiet line that marks the swap after an answer this session. */
export function HowHeardThanks() {
  return (
    <div className="hh-thanks">
      <span className="hh-thanks-mark" aria-hidden="true">✓</span> Thanks — that helps.
    </div>
  );
}

// ---- passphrase gate (Stage A) ----
// The one time Aurora shows the viewer key. Extracted from RunHandoff so the
// SYNC live view can run the same ceremony: without it, a sync user who
// closed the tab mid-run had a consumed code and an emailed link that opens
// nothing (the server discards its copy at completion). The checkbox ARMS the
// button; the button commits — two deliberate beats, same as the hand-off.
export function PassphraseGate({
  passphrase,
  restarted,
  onSaved,
  durationPhrase = "it takes about an hour",
  runComplete = false,
}: {
  passphrase: string;
  restarted?: boolean;
  onSaved: () => void;
  /** Run-length phrase inside the lede — the batched hand-off says "about an
   *  hour"; the sync live view says "usually 15–30 minutes". */
  durationPhrase?: string;
  /** True when this gate is holding a screen that would otherwise already be
   *  showing results (irrecoverable-loss guard, 2026-07-04): the run finished
   *  underneath an un-acked gate, so the lede must not claim it's still
   *  running — it says "just finished" instead of quoting durationPhrase. */
  runComplete?: boolean;
}) {
  const [ackArmed, setAckArmed] = useState(false);
  return (
    <div className="rs rs-handoff">
      {/* Honest keep-tab-open reason (2026-07-04): this gate IS the reason —
          Aurora is genuinely showing the passphrase right here, right now,
          so "for now" and "Aurora will show it to you here" are both true
          in this exact spot (unlike RunState's own fallback banner, which
          can't make either promise — see the comment there). Reuses the
          existing amber --pending chrome (same "temporary, about to clear"
          shape as the client-side-promotion hold), no new chrome invented. */}
      <div className="rs-keepopen-banner rs-keepopen-banner--pending" role="note">
        <span className="rs-keepopen-banner-ico" aria-hidden="true" />
        <span className="rs-keepopen-banner-txt">
          <b>Keep this tab open for now — your passphrase isn&rsquo;t saved yet.</b>
          Aurora will show it to you here; once you&rsquo;ve saved it,
          you&rsquo;re free to go.
        </span>
      </div>
      <h1 className="rs-handoff-title">First, save your passphrase</h1>
      <div className="rs-handoff-pass">
        <p>
          {runComplete ? (
            <>Your analysis just finished, and the</>
          ) : (
            <>Your analysis is running — {durationPhrase}, and the</>
          )}{" "}
          finished file will be locked with this passphrase. Aurora
          won&rsquo;t show it again after you leave. We don&rsquo;t keep a
          copy, so put it somewhere safe (a password manager, or a note only
          you can&nbsp;see).
        </p>
        {restarted && (
          <p className="rs-handoff-pass-replaces">
            Starting over makes a fresh passphrase — this one. If you
            saved one from your earlier try, replace it; the old one
            doesn&rsquo;t open anything.
          </p>
        )}
        <div className="rs-handoff-pass-row">
          <code className="rs-handoff-pass-value">{passphrase}</code>
          <CopyButton text={passphrase} />
        </div>
        <label className="rs-handoff-pass-ack">
          <input
            type="checkbox"
            checked={ackArmed}
            onChange={(e) => setAckArmed(e.target.checked)}
          />
          <span>I&rsquo;ve saved it somewhere safe.</span>
        </label>
        <button
          type="button"
          className="rs-handoff-pass-continue"
          disabled={!ackArmed}
          onClick={onSaved}
        >
          {/* Two different actions must not share the name "Continue"
              (Rich, 2026-07-22 staging drill): when the run has already
              finished under the gate, this click REVEALS the results screen
              while the wizard's own black "Continue →" waits below it — so
              say what it does. Mid-run there's no competing footer button
              (it reads "Running…", disabled), and after acking the user
              stays on the live view, so the original label stands. */}
          {runComplete ? "Show my results →" : "Continue — I have my passphrase"}
        </button>
      </div>
    </div>
  );
}

// ---- container: the whole "Read the messages" running view ----
export function RunState({
  phase,
  surfaced,
  patternCount,
  grounded,
  groundedTotal,
  elapsedLabel,
  estimate = "Usually 15–30 minutes.",
  leave = "hold",
  email,
  sponsored = false,
  howHeard,
}: {
  phase: RunPhase;
  surfaced: boolean;
  patternCount: number | null;
  grounded: number | null;
  groundedTotal: number | null;
  elapsedLabel: string;
  estimate?: string;
  /** "hold" (default): the tab is required and no email exit is coming —
   *  ready emails off or no address; the only door is staying.
   *  "pending": the tab is still required (the client-driven preview→convert
   *  leg) BUT the email exit is coming — same staged messaging as the batch
   *  hand-off ("turns green the moment leaving is safe, usually within a few
   *  minutes"), so the user knows to wait rather than despair.
   *  "safe": the chain runs server-side, the ready email will send, and the
   *  passphrase ceremony is done — leaving loses nothing (the App computes
   *  this; the unload guard in lib/unload-guard is its exact complement). */
  leave?: "hold" | "pending" | "safe";
  /** Where the ready email goes — shown in the safe-to-leave banner. */
  email?: string | null;
  /** True when this run is covered without a charge (sponsored/free code, no
   *  payment this visit) — the leave-now reassurance must not mention a
   *  payment that never existed (review, 2026-07-10). */
  sponsored?: boolean;
  /** The optional "where did you hear about Aurora?" question — same
   *  contract as RunHandoff's. Sync transport is the PRODUCTION default
   *  (AURORA_FANOUT_BATCHED=0 since the 2026-07-01 cutover), so this view
   *  is where most real users wait; without it here the question would be
   *  dark in prod. Same one-slot rule against the rotator below. */
  howHeard?: HowHeardProps;
}) {
  const idx: number = phase < 0 ? 0 : phase;
  const title = RUN_PHASES[idx] ?? RUN_PHASES[0];
  return (
    <div className="rs">
      {leave === "safe" ? (
        <div
          className="rs-keepopen-banner rs-keepopen-banner--free"
          role="note"
        >
          <span className="rs-keepopen-banner-ico" aria-hidden="true" />
          <span className="rs-keepopen-banner-txt">
            <b>Stay or go — both work</b>
            The analysis runs on our servers from here. You can stay and
            watch, or close this tab —{" "}
            {email
              ? `we'll email you at ${email} the moment it's ready.`
              : `we'll email you the moment it's ready.`}
          </span>
        </div>
      ) : leave === "pending" ? (
        <div
          className="rs-keepopen-banner rs-keepopen-banner--pending"
          role="note"
        >
          <span className="rs-keepopen-banner-ico" aria-hidden="true" />
          <span className="rs-keepopen-banner-txt">
            <b>Stay on this page for now — you can leave soon</b>
            Aurora is reading your record in this tab, and this first part
            needs it open. The banner turns green the moment leaving is safe —
            usually within a few minutes —{" "}
            {email
              ? `and from then on we'll email you at ${email} when it's ready.`
              : `and from then on we'll email you when it's ready.`}{" "}
            And if you do have to leave now: nothing is lost —{" "}
            {sponsored ? "" : "your payment holds, and "}this page picks up
            where it left off when you come back.
          </span>
        </div>
      ) : (
        // No email channel exists yet (ready emails off, or no address on
        // file) — the ONE case where staying really is required for the
        // whole run, no matter the passphrase. Kept deliberately un-linked
        // to passSaved (2026-07-04 copy fix): the old wording implied the
        // tab itself was doing fragile in-browser work ("don't navigate
        // away while it works"), which hasn't been true since the sync
        // cutover — the chain runs server-side regardless. The honest
        // reason to stay here is narrower: there's simply nowhere else for
        // the result to go, so this tab is the only place to see it land.
        <div className="rs-keepopen-banner" role="note">
          <span className="rs-keepopen-banner-ico" aria-hidden="true" />
          <span className="rs-keepopen-banner-txt">
            <b>Keep this tab open</b>
            The analysis runs safely on our servers, but this tab is the
            only place you&rsquo;ll see it finish — please stay until
            it&rsquo;s done.
          </span>
        </div>
      )}
      <div className="rs-head">
        <span className="rs-title">{title}</span>
        <span className="rs-clk">{elapsedLabel}</span>
      </div>
      <div className="rs-est">
        ⏱ <b>{estimate}</b>
      </div>
      <div className="rs-status">
        <PhaseRail phase={phase < 0 ? 0 : phase} />
      </div>
      <MilestoneBadges
        surfaced={surfaced}
        patternCount={patternCount}
        grounded={grounded}
        groundedTotal={groundedTotal}
      />
      {howHeard?.state === "open" ? (
        <HowHeardCard onAnswer={howHeard.onAnswer} onSkip={howHeard.onSkip} />
      ) : (
        <>
          {howHeard?.state === "thanked" && <HowHeardThanks />}
          <EducationRotator />
        </>
      )}
    </div>
  );
}

// ---- batched hand-off view ----
// Batched runs take up to ~an hour, so there's nothing to watch live. This
// REPLACES the RunState live view (no keep-tab-open banner, no elapsed timer,
// no phase rail, no education carousel — all of which invite the user to sit
// and stare at an hour-long bar). It's a calm hand-off: it's running, here's
// how you get it back, you're free to go. Shown when /healthz reports
// fanout_batched (i.e. the deployed server is batched). A single quiet pulse is
// the only live element, for anyone who keeps the tab open.
/** One-tap copy for the passphrase (UX audit D). Clipboard API with a
 *  select-it-yourself fallback message — survivors should never have to
 *  hand-transcribe word-word-word. */
function CopyButton({ text }: { text: string }) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  return (
    <button
      type="button"
      className="rs-handoff-pass-copy"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setState("copied");
        } catch {
          setState("failed");
        }
        window.setTimeout(() => setState("idle"), 2500);
      }}
    >
      {state === "copied" ? "Copied ✓" : state === "failed" ? "Select it and copy" : "Copy"}
    </button>
  );
}

export function RunHandoff({
  email,
  onChangeEmail,
  passphrase,
  convertRunning,
  passSaved: passSavedProp,
  onPassSavedChange,
  events = [],
  startedAt,
  readyEmailEnabled = false,
  restarted = false,
  howHeard,
}: {
  /** The address the ready email's come-back link goes to. */
  email: string | null;
  /** Open the change-email modal. Omitted → no "Change" affordance. */
  onChangeEmail?: () => void;
  /** Submit-time viewer passphrase — shown HERE, once. Aurora discards its
   *  copy when the finished file is locked with it, so the emailed link
   *  alone can't open anything. Null on resumed sessions (already shown). */
  passphrase?: string | null;
  /** True once the full run is launched server-side. BEFORE that (the
   *  setup beat + the discovery preview) the preview→convert promotion is
   *  client-side — the open tab re-uploads the corpus — so closing the tab
   *  would strand the run (audit B5). Gates "safe to close". */
  convertRunning?: boolean;
  /** Lifted ack (App owns it so the beforeunload guard can see it). When
   *  omitted, falls back to local state — keeps the component standalone. */
  passSaved?: boolean;
  onPassSavedChange?: (saved: boolean) => void;
  /** This phase's accumulated SSE events — drives the milestone list
   *  (lib/run-milestones). Replayed from 0 on resume, so reloads keep
   *  their place. */
  events?: SseEvent[];
  /** ISO start time, when known — shown in the status line. */
  startedAt?: string | null;
  /** This run follows a beat-1 cancel-and-restart: the gate notes that
   *  this passphrase REPLACES the earlier run's (each run has its own
   *  key; the canceled one opens nothing). */
  restarted?: boolean;
  /** S13: whether the server will ACTUALLY send the ready email (the
   *  /healthz `ready_email_enabled` flag, default OFF). The "close this
   *  tab — we'll email you" promise renders only when true; otherwise the
   *  hand-off keeps the stay-on-this-page framing so the copy never
   *  promises an email the operator hasn't enabled. */
  readyEmailEnabled?: boolean;
  /** The optional "where did you hear about Aurora?" question. Omitted →
   *  never rendered (keeps the component standalone). App owns the state,
   *  the POST, and the localStorage latch. */
  howHeard?: HowHeardProps;
}) {
  const milestones = deriveMilestones({ convertRunning: !!convertRunning, events });
  const startedLabel = (() => {
    if (!startedAt) return null;
    const d = new Date(startedAt);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  })();
  const hasEmail = !!email;
  // "Safe to close" must not appear until (a) the run no longer needs this
  // tab (convert running server-side) AND (b) the user confirms they saved
  // the passphrase — closing without it makes the result unopenable.
  const [passSavedLocal, setPassSavedLocal] = useState(false);
  const passSaved = passSavedProp ?? passSavedLocal;
  const setPassSaved = onPassSavedChange ?? setPassSavedLocal;
  const safeToClose =
    !!convertRunning && hasEmail && readyEmailEnabled && (!passphrase || passSaved);
  // Two-doors flow (Rich's pick from handoff-mocks-v2): the HOLD→GO flip is
  // a color-coded BANNER (amber ⏳ → green ✓) with an explicit stay/leave
  // choice; choosing "Stay and watch" collapses into the compact progress
  // view (Direction 3). No "Working" badge anywhere — state lives in the
  // banner + the pulsing milestone. The passphrase gate confirms in two
  // beats: the checkbox ARMS a button; the button commits the collapse.
  const needsPassAck = !!passphrase && !passSaved;
  const [showPass, setShowPass] = useState(false);
  const [stayed, setStayed] = useState(false);

  const startedLabelShort = startedLabel;
  const usuallyBy = (() => {
    if (!startedAt) return null;
    const d = new Date(startedAt);
    if (Number.isNaN(d.getTime())) return null;
    const eta = new Date(d.getTime() + 60 * 60_000);
    return eta.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  })();

  // One quiet meta line, not pill badges (Rich, wait-screen mock 2026-06-11):
  // the three facts cost ~110px as wrapping chips; as a single small-text
  // row they cost one line and the links still work the same.
  const chips = (
    <div className="rs-meta">
      {passphrase && (
        <span className="rs-meta-item">
          <span aria-hidden="true">🔑</span> <b>Passphrase saved</b>
          <button type="button" className="rs-chip-lnk" onClick={() => setShowPass((v) => !v)}>
            {showPass ? "hide" : "show"}
          </button>
        </span>
      )}
      {hasEmail && (
        <span className="rs-meta-item rs-meta-item--email">
          <span aria-hidden="true">✉</span> {email}
          {onChangeEmail && (
            <button type="button" className="rs-chip-lnk" onClick={onChangeEmail}>
              change
            </button>
          )}
        </span>
      )}
      {startedLabelShort && (
        <span className="rs-meta-item">
          <span aria-hidden="true">⏱</span> started {startedLabelShort}
          {usuallyBy ? <> · usually done by ~{usuallyBy}</> : null}
        </span>
      )}
    </div>
  );
  const passReveal = passphrase && showPass && (
    <div className="rs-handoff-pass-row rs-handoff-pass-row--again">
      <code className="rs-handoff-pass-value">{passphrase}</code>
      <CopyButton text={passphrase} />
    </div>
  );
  const milesList = (
    <ul className="rs-miles">
      {milestones.map((m) => (
        <li key={m.key} className={`rs-miles-${m.state}`}>
          <span className="rs-miles-mark" aria-hidden="true">
            {m.state === "done" ? "✓" : m.state === "now" ? "●" : "○"}
          </span>
          <span>{m.label}</span>
          {m.caption && <span className="rs-miles-when">{m.caption}</span>}
        </li>
      ))}
    </ul>
  );

  // Stage A — the passphrase gate (shared with the sync live view).
  if (needsPassAck) {
    return (
      <PassphraseGate
        passphrase={passphrase!}
        restarted={restarted}
        onSaved={() => setPassSaved(true)}
      />
    );
  }

  // GO, before choosing: the two doors.
  if (safeToClose && !stayed) {
    return (
      <div className="rs rs-handoff">
        <div className="rs-banner rs-banner--go">
          <span className="rs-banner-ico" aria-hidden="true">✓</span>
          <div>
            <h1>Your part is done. Aurora takes it from&nbsp;here.</h1>
            <p>
              The analysis finishes on its own in about an hour. Stay or go —
              both&nbsp;work:
            </p>
            <div className="rs-doors">
              <div className="rs-door rs-door--primary">
                <b>✉&nbsp;&nbsp;Close this tab — we&rsquo;ll email you</b>
                <span>
                  A private link goes to <u>{email}</u> from
                  Aurora&nbsp;&lt;hello@mail.plaintosee.cc&gt;. Check your junk
                  folder the first&nbsp;time.
                </span>
              </div>
              <button type="button" className="rs-door" onClick={() => setStayed(true)}>
                <b>Stay and watch →</b>
                <span>The analysis opens right here when it&rsquo;s done.</span>
              </button>
            </div>
          </div>
        </div>
        {chips}
        {passReveal}
        {/* No carousel on this screen, so the question simply follows the
            meta line. Same card, same one-shot state as the stayed view. */}
        {howHeard?.state === "open" && (
          <HowHeardCard onAnswer={howHeard.onAnswer} onSkip={howHeard.onSkip} />
        )}
        {howHeard?.state === "thanked" && <HowHeardThanks />}
      </div>
    );
  }

  // HOLD (pre-convert, or no-email keep-open) and the stayed progress view.
  return (
    <div className="rs rs-handoff">
      {safeToClose ? (
        <div className="rs-banner rs-banner--go rs-banner--slim">
          <span className="rs-banner-ico" aria-hidden="true">✓</span>
          <div>
            <h1>
              Safe to leave — we&rsquo;ll email you. Staying? It opens&nbsp;here.
            </h1>
          </div>
        </div>
      ) : hasEmail && readyEmailEnabled ? (
        <div className="rs-banner rs-banner--hold">
          <span className="rs-banner-ico" aria-hidden="true">⏳</span>
          <div>
            <h1>A short setup step before you can&nbsp;go</h1>
            <p>
              <b>Stay on this page for now</b> — this banner turns green the
              moment leaving is safe, usually within a few&nbsp;minutes.
            </p>
          </div>
        </div>
      ) : (
        <div className="rs-banner rs-banner--hold">
          <span className="rs-banner-ico" aria-hidden="true">⏳</span>
          <div>
            <h1>Keep this tab open until it&nbsp;finishes</h1>
            <p>
              Your analysis will appear right here when it&rsquo;s done.
              {/* The add-an-email invitation is itself an email promise —
                  it only renders when the server will actually send (S13). */}
              {onChangeEmail && readyEmailEnabled && (
                <>
                  {" "}
                  Or add an email and we&rsquo;ll send you a private link —
                  then you can close this&nbsp;tab.
                  <button
                    type="button"
                    className="rs-handoff-change"
                    onClick={onChangeEmail}
                  >
                    Add an email
                  </button>
                </>
              )}
            </p>
          </div>
        </div>
      )}
      {chips}
      {passReveal}
      {milesList}
      {/* One slot, one occupant (Rich, 2026-08-06): while the question is
          open it HOLDS the carousel's position at the carousel's size; an
          answer or a skip swaps the carousel in — with a quiet thanks line
          above it on answer, silently on skip. */}
      {(safeToClose || !hasEmail || !readyEmailEnabled) && convertRunning && (
        howHeard?.state === "open" ? (
          <HowHeardCard onAnswer={howHeard.onAnswer} onSkip={howHeard.onSkip} />
        ) : (
          <>
            {howHeard?.state === "thanked" && <HowHeardThanks />}
            <EducationRotator compact />
          </>
        )
      )}
    </div>
  );
}

// ---- elapsed clock: the one genuinely-continuous real signal ----
// Starts when `active` goes true, resets when it goes false. mm:ss.
export function useElapsedLabel(active: boolean): string {
  const [label, setLabel] = useState("0:00");
  const startRef = useRef<number | null>(null);
  useEffect(() => {
    if (!active) {
      startRef.current = null;
      return;
    }
    if (startRef.current === null) startRef.current = Date.now();
    const tick = () => {
      const start = startRef.current ?? Date.now();
      const s = Math.max(0, Math.floor((Date.now() - start) / 1000));
      setLabel(`${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`);
    };
    tick();
    const t = window.setInterval(tick, 1000);
    return () => window.clearInterval(t);
  }, [active]);
  return label;
}

// pattern_id slug → human title. "class-c.child-as-messenger" → "Child as messenger".
// (Server-side the catalog has richer display_names; use those if the payload ever
// carries them. This is the always-available fallback.)
export function slugToTitle(patternId: string): string {
  const base = patternId.includes(".")
    ? patternId.slice(patternId.lastIndexOf(".") + 1)
    : patternId;
  const words = base.replace(/[-_]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : patternId;
}

export type RunMode =
  | "setup"
  | "running"
  | "empty"
  | "complete"
  | "failed"
  | "cancelled";

// Pure: maps the run's primitive state (phase.kind + SSE events + the caller's
// pattern count) to the view. Unit-tested in RunState.test.tsx. Grounding totals
// ride on the fan-out stage_done event; phase 1→2 (Grounding→Review) advances
// when fan-out or audit completes (the only sub-stage signals we get).
//
// The server (events.py) emits ONE event type — "stage_done" — discriminated by
// data.stage ("discovery" | "fanout" | "audit"). This used to match on
// "fanout_done" / "audit_done" event TYPES that the server never emits, so the
// grounded "X of Y" badge AND the Review-phase advance were both dead in
// production (the unit tests passed only because they mocked the wrong shape).
// Pure: discovered patterns that will NOT be in the packet. Discovery's list
// (previewSnapshot.patterns) feeds the results card, but delivery is decided
// later: the fan-out's weak-pattern guard drops a finding with no anchored
// claims (`patterns_dropped` — the guard's own line, parsed server-side), and a
// pattern whose grounding errored has no finding at all. Without this the card
// listed nine patterns for a viewer carrying eight (staging, 2026-09-08). A
// pattern's LAST pattern_progress status wins, so a retried-then-done pattern
// still counts as delivered.
export function undeliveredPatternIds(
  events: ReadonlyArray<{ type: string; data: unknown }>,
): Set<string> {
  const out = new Set<string>();
  const lastStatus = new Map<string, string>();
  for (const e of events) {
    const d = e.data as
      | { pattern_ids?: unknown; pattern_id?: unknown; status?: unknown }
      | null;
    if (!d || typeof d !== "object") continue;
    if (e.type === "patterns_dropped" && Array.isArray(d.pattern_ids)) {
      for (const id of d.pattern_ids) if (typeof id === "string") out.add(id);
    } else if (
      e.type === "pattern_progress" &&
      typeof d.pattern_id === "string" &&
      typeof d.status === "string"
    ) {
      lastStatus.set(d.pattern_id, d.status);
    }
  }
  for (const [id, status] of lastStatus) if (status === "error") out.add(id);
  return out;
}

export function deriveRunView(
  kind: string,
  events: ReadonlyArray<{ type: string; data: unknown }>,
  patternCount: number | null,
  // True when the user themselves stopped the run. The server has no
  // distinct canceled state (cancel marks *_failed, exit 130), so the
  // caller derives this from its own cancel request, the status
  // exit_code, or the chain's "cancelled" event (also checked here).
  cancelledByUser = false,
): {
  mode: RunMode;
  phase: RunPhase;
  surfaced: boolean;
  patternCount: number | null;
  grounded: number | null;
  groundedTotal: number | null;
  failedReason: string | null;
} {
  let grounded: number | null = null;
  let groundedTotal: number | null = null;
  let sawFanoutOrAudit = false;
  for (const e of events) {
    if (e.type !== "stage_done") continue;
    const d = e.data as {
      stage?: string;
      findings_accepted?: number;
      findings_total?: number;
    } | null;
    if (!d) continue;
    if (d.stage === "fanout") {
      sawFanoutOrAudit = true;
      if (typeof d.findings_accepted === "number") grounded = d.findings_accepted;
      if (typeof d.findings_total === "number") groundedTotal = d.findings_total;
    } else if (d.stage === "audit") {
      sawFanoutOrAudit = true;
    }
  }
  const surfaced = patternCount !== null;
  const base = { surfaced, patternCount, grounded, groundedTotal, failedReason: null };
  switch (kind) {
    case "preview-running":
      return { mode: "running", phase: 0, ...base };
    case "preview-ready":
      // Discovery finished. >0 patterns auto-promotes to convert (a brief
      // running flash). 0 patterns is TERMINAL — there's nothing to ground, so
      // surface an empty state instead of hanging on "Grounding" forever.
      return patternCount === 0
        ? { mode: "empty", phase: 0, ...base }
        : { mode: "running", phase: 1, ...base };
    case "convert-running":
      return { mode: "running", phase: sawFanoutOrAudit ? 2 : 1, ...base };
    case "run-complete":
      return { mode: "complete", phase: 2, ...base };
    case "preview-failed": {
      const cancelled =
        cancelledByUser || events.some((e) => e.type === "cancelled");
      return cancelled
        ? { mode: "cancelled", phase: 0, ...base }
        : { mode: "failed", phase: 0, ...base, failedReason: "reading your messages" };
    }
    case "convert-failed": {
      const cancelled =
        cancelledByUser || events.some((e) => e.type === "cancelled");
      return cancelled
        ? { mode: "cancelled", phase: 1, ...base }
        : { mode: "failed", phase: 1, ...base, failedReason: "grounding the patterns" };
    }
    default:
      return {
        mode: "setup",
        phase: -1,
        surfaced: false,
        patternCount: null,
        grounded: null,
        groundedTotal: null,
        failedReason: null,
      };
  }
}

// Pure: which of the five run-step surfaces to paint. Split out of App.tsx's
// inline ternary because the inline gate had a hole — it keyed the hand-off on
// `mode === "running"`, but clicking Submit renders the run step while the run
// is still being kicked off (phase "setup", mode "setup") for a beat. During
// that window the gate fell through to the legacy live monitor ("Keep this tab
// open / ~10 min"), the exact wrong framing for a batched email-when-ready run
// — and it showed on EVERY run, in any browser (prod, 2026-06-09). Terminal
// modes (complete/empty/failed) decide first; for the two non-terminal modes
// (setup + running) a batched server ALWAYS hands off. The live monitor is for
// non-batched servers only.
export type RunStepScreen =
  | "results"
  | "empty"
  | "failed"
  | "cancelled"
  | "handoff"
  | "live";
export function selectRunStepScreen(
  mode: RunMode,
  fanoutBatched: boolean,
): RunStepScreen {
  if (mode === "complete") return "results";
  if (mode === "empty") return "empty";
  if (mode === "failed") return "failed";
  // The user stopped it themselves — a calm acknowledgment, never the
  // "something went wrong" failure banner (live, 2026-06-11).
  if (mode === "cancelled") return "cancelled";
  // mode is "setup" or "running" here — both hand off when batched.
  return fanoutBatched ? "handoff" : "live";
}

// The terminal screens that can swap in the instant a run finishes without
// ever having shown (or fully acked) the passphrase gate. "failed",
// "cancelled", AND "empty" are deliberately excluded — none of the three ever
// produces a viewer file (a stopped run has nothing for the passphrase to
// unlock, whoever or whatever stopped it; a 0-pattern preview never gets
// promoted to convert, so no file is ever built either), so holding any of
// them behind the ceremony would show a live-sounding "your analysis just
// finished, and the finished file will be locked with this passphrase" on a
// run that produced — and will never produce — anything for that passphrase
// to open (cancel-path fix, 2026-07-04 — this used to include "cancelled",
// which re-ran the gate on a stop that arrived while it was up, including a
// stop the user never clicked — the render condition only looks at
// runStepScreen, not who or what triggered the terminal transition; "empty"
// evicted the same day, launch-audit review finding-low, for the identical
// reason applied to a 0-pattern result instead of a stop).
// "handoff" and "live" hold their own in-flight gate already (RunHandoff's
// needsPassAck / App's sync PassphraseGate branch).
const PASS_ACK_TERMINAL_SCREENS: ReadonlySet<RunStepScreen> = new Set([
  "results",
]);

// Pure: irrecoverable-loss guard (pre-launch audit fix, 2026-07-04). The
// server discards its copy of the viewer passphrase the moment the run
// completes, and it's shown to the user exactly once. Before this fix,
// App's inline ternary painted "results" (or "empty") the instant
// selectRunStepScreen said so — even mid-ceremony, even when the gate had
// NEVER shown at all (no linked email — the sync live view only ran the
// gate when an email existed to make leaving mid-run safe). Either way the
// passphrase vanished from screen with no way back. This holds the terminal
// screen open behind the (same, shared) PassphraseGate — with runComplete
// copy — until the user explicitly acks, regardless of whether the gate
// ever ran during the live phase. Cancelled/failed runs are excluded (see
// PASS_ACK_TERMINAL_SCREENS above) — there's no file for either to lose.
export function shouldHoldForPassAck(
  screen: RunStepScreen,
  hasPassphrase: boolean,
  passSaved: boolean,
): boolean {
  return hasPassphrase && !passSaved && PASS_ACK_TERMINAL_SCREENS.has(screen);
}

// ---- results (run-complete): confident summary. Names + grounded aggregate +
// provenance + caveat; NO quotes/claims (those live in the encrypted viewer). ----
export function ResultsSummary({
  patternNames,
  patternCount,
  grounded,
  groundedTotal,
}: {
  patternNames: string[];
  patternCount: number;
  grounded: number | null;
  groundedTotal: number | null;
}) {
  const groundedClause =
    grounded !== null && groundedTotal !== null
      ? ` Each is grounded in verbatim quotes — ${grounded} of ${groundedTotal} cited messages checked.`
      : "";
  return (
    <div className="rs">
      <div className="rs-head">
        <span className="rs-title">What Aurora found</span>
      </div>
      <p className="rs-results-summary">
        Aurora surfaced{" "}
        <b>
          {patternCount} recurring pattern{patternCount === 1 ? "" : "s"}
        </b>{" "}
        across your record.{groundedClause}
      </p>
      {patternNames.length > 0 && (
        <ul className="rs-pat-list">
          {patternNames.map((nm, i) => (
            <li key={`${nm}-${i}`}>{nm}</li>
          ))}
        </ul>
      )}
      <p className="rs-caveat-line">
        <b>What this is and isn’t.</b> Aurora surfaces what’s in the record — it won’t
        invent patterns, and a pattern it doesn’t name isn’t proof none exists. This
        isn’t a diagnosis or legal advice. The quotes and full reasoning live in the
        encrypted viewer you’ll build next, which stays on your device.
      </p>
    </div>
  );
}

// ---- empty (discovery finished, 0 patterns) — a real terminal state, not a
// hung "Grounding". Non-diagnostic; doesn't over-claim "nothing happened". ----
export function EmptyResult() {
  return (
    <div className="rs">
      <div className="rs-head">
        <span className="rs-title">No recurring patterns surfaced</span>
      </div>
      <p className="rs-results-summary">
        Aurora read the whole record but didn’t surface any recurring patterns
        this time.
      </p>
      <p className="rs-caveat-line">
        A clean result <b>isn’t proof that nothing happened</b> — it can mean the
        record is short, the behaviors aren’t the repeated kind Aurora looks for,
        or the analysis simply didn’t land. If you expected patterns here, send
        feedback from the header and we’ll take a look.
      </p>
    </div>
  );
}
